import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Trash2, Pencil, AlertTriangle, ShieldCheck, ChevronDown, ScrollText, CheckCircle2, Loader2, Paperclip } from "lucide-react";
import { ExpiryBadge } from "@/components/ExpiryBadge";
import { confirmAction } from "@/components/ConfirmHost";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/permissions";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

const CATEGORIES = ["Health & Safety", "GDPR / Data protection", "Drug & Alcohol", "Driver Handbook", "Equality & Diversity",
  "Environmental", "Safeguarding", "Anti-bribery", "Whistleblowing", "Disciplinary & Grievance", "Vehicle use", "Other"];
const PROCEDURE_CATEGORIES = ["Collection & delivery", "Proof of delivery", "Vehicle checks", "Accident / incident", "Fuel & mileage",
  "Loading & securing", "Waste handling", "Cash & payments", "Customer complaints", "Onboarding", "Other"];

const VARIANTS = {
  policy: {
    kind: "policy", one: "policy", many: "policies", Title: "Policies", docLabel: "Policy documents", categories: null,
    intro: "Company policy documents with review dates — you'll be warned 30 days before a review is due.",
    titlePh: "e.g. Health & Safety Policy",
  },
  procedure: {
    kind: "procedure", one: "procedure", many: "procedures", Title: "Procedures", docLabel: "Procedure documents", categories: PROCEDURE_CATEGORIES,
    intro: "Step-by-step operating procedures with review dates — you'll be warned 30 days before a review is due.",
    titlePh: "e.g. Vehicle Daily Walkaround Check",
  },
};
const plural = (cfg, n) => (n === 1 ? cfg.one : cfg.many);

const EMPTY = { title: "", category: "", version: "", doc_number: "", owner: "", effective_date: "", last_reviewed: "", review_date: "", review_every_months: 12, description: "", is_active: true };

export default function Policies({ variant = "policy" }) {
  const cfg = VARIANTS[variant];
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [p, a] = await Promise.all([api.get("/policies", { params: { kind: cfg.kind } }), api.get("/policies/alerts", { params: { kind: cfg.kind } })]);
      setRows(p.data || []); setAlerts(a.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); }, [variant]); // eslint-disable-line

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    return s ? rows.filter((p) => `${p.title} ${p.category} ${p.owner} ${p.version} ${p.doc_number || ""}`.toLowerCase().includes(s)) : rows;
  }, [rows, q]);

  return (
    <div data-testid="policies-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Compliance</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1">{cfg.Title}</h1>
          <p className="text-slate-600 mt-2 text-sm">{cfg.intro}</p>
        </div>
        <Button data-testid="policy-add-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setEditing(EMPTY)}>
          <Plus className="h-4 w-4 mr-2" /> Add {cfg.one}
        </Button>
      </div>

      <PolicyAlerts alerts={alerts} cfg={cfg} />

      <div className="flex items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="policy-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, category, owner" className="pl-9 rounded-sm border-slate-300 bg-white" />
        </div>
        <div className="ml-auto text-xs text-slate-500" data-testid="policy-count">{filtered.length} {plural(cfg, filtered.length)}</div>
      </div>

      <div className="space-y-3">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <Card className="rounded-sm border border-dashed border-slate-300 shadow-none p-10 text-center text-sm text-slate-500" data-testid="policy-empty">
            No {cfg.many} yet. Click &quot;Add {cfg.one}&quot; to upload your first one.
          </Card>
        )}
        {filtered.map((p) => <PolicyBox key={p.id} p={p} cfg={cfg} onEdit={() => setEditing(p)} onChanged={load} />)}
      </div>

      <PolicyDialog cfg={cfg} policy={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function PolicyAlerts({ alerts, cfg }) {
  if (!alerts.length) {
    return (
      <Card className="rounded-sm border border-emerald-200 bg-emerald-50/50 shadow-none p-4 flex items-center gap-2 text-sm text-emerald-800" data-testid="policy-alerts-clear">
        <ShieldCheck className="h-4 w-4" /> All {cfg.many} are in date — no reviews due in the next 30 days.
      </Card>
    );
  }
  return (
    <Card className="rounded-sm border border-amber-200 shadow-none" data-testid="policy-alerts">
      <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="h-4 w-4" /> {alerts.length} {plural(cfg, alerts.length)} overdue or due for review within 30 days
      </div>
      <ul className="divide-y divide-slate-100">
        {alerts.map((a) => (
          <li key={a.policy_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`policy-alert-${a.policy_id}`}>
            <span className={`h-2 w-2 rounded-full shrink-0 ${a.tone === "red" ? "bg-red-500" : "bg-amber-500"}`} />
            <span className="font-medium text-slate-900 flex-1 truncate">{a.title}</span>
            <span className="text-xs text-slate-500">Review {fmtDate(a.review_date)}</span>
            <span className={`text-xs font-semibold tabular-nums w-28 text-right ${a.tone === "red" ? "text-red-600" : "text-amber-700"}`}>
              {a.days < 0 ? `Overdue ${-a.days}d` : a.days === 0 ? "Due today" : `Due in ${a.days}d`}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PolicyBox({ p, cfg, onEdit, onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reviewed = async () => {
    setBusy(true);
    try { await api.post(`/policies/${p.id}/reviewed`); toast.success(`${p.title} marked as reviewed`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!(await confirmAction(`Delete "${p.title}" and all its files? This can't be undone.`))) return;
    try { await api.delete(`/policies/${p.id}`); toast.success("Policy deleted"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className={`rounded-sm border border-slate-200 shadow-none overflow-hidden ${p.is_active === false ? "opacity-60" : ""}`} data-testid={`policy-box-${p.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`policy-toggle-${p.id}`}
        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-slate-50 transition-colors">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-slate-900 text-white shrink-0"><ScrollText className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{p.title}{p.is_active === false && <span className="ml-2 text-xs text-slate-500">(archived)</span>}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{[p.category, p.doc_number && `No. ${p.doc_number}`, p.version && `v${p.version}`, p.owner].filter(Boolean).join(" · ") || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-5 shrink-0">
          <span className="text-xs text-slate-600"><Paperclip className="h-3 w-3 inline mr-1" />{p.file_count || 0}</span>
          <div className="w-32"><ExpiryBadge date={p.review_date} testid={`policy-review-${p.id}`} pastLabel="Overdue" /></div>
          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 border-t border-slate-100 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <Info label="Document number" value={p.doc_number} />
            <Info label="Effective from" value={fmtDate(p.effective_date)} />
            <Info label="Last reviewed" value={fmtDate(p.last_reviewed)} />
            <Info label="Next review" value={fmtDate(p.review_date)} />
            <Info label="Review every" value={`${p.review_every_months || 12} months`} />
          </div>
          {p.description && <p className="text-sm text-slate-700 whitespace-pre-line">{p.description}</p>}
          <div className="flex flex-wrap gap-2">
            <Button data-testid={`policy-reviewed-${p.id}`} size="sm" variant="outline" className="rounded-sm" onClick={reviewed} disabled={busy}>
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}Mark as reviewed today
            </Button>
            <Button data-testid={`policy-edit-${p.id}`} size="sm" variant="outline" className="rounded-sm" onClick={onEdit}><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit</Button>
            {isOwner(user) && <Button data-testid={`policy-delete-${p.id}`} size="sm" variant="outline" className="rounded-sm text-red-600" onClick={remove}><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete</Button>}
          </div>
          <FileUploader entityType={cfg.kind} entityId={p.id} label={cfg.docLabel} showDocNumber onChange={(files) => { if (files.length !== (p.file_count || 0)) onChanged(); }} />
        </div>
      )}
    </Card>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="font-medium text-slate-900 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function PolicyDialog({ cfg, policy, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (policy) setForm({ ...EMPTY, ...policy }); }, [policy]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, kind: cfg.kind, review_every_months: Number(form.review_every_months) || 12 };
      if (policy.id) await api.put(`/policies/${policy.id}`, body);
      else await api.post("/policies", body);
      toast.success(policy.id ? "Saved" : "Added — open it to upload the document");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={`policy-field-${k}`} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-sm border-slate-300" {...props} />
    </div>
  );

  return (
    <Dialog open={!!policy} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="policy-dialog">
        <DialogHeader><DialogTitle>{policy?.id ? `Edit ${cfg.one}` : `Add ${cfg.one}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">{field("title", `${cfg.one[0].toUpperCase()}${cfg.one.slice(1)} title`, { placeholder: cfg.titlePh })}</div>
          {field("category", "Category", { list: "policy-categories", placeholder: "Pick or type" })}
          <datalist id="policy-categories">{(cfg.categories || CATEGORIES).map((c) => <option key={c} value={c} />)}</datalist>
          {field("version", "Version", { placeholder: "e.g. 2.1" })}
          {field("doc_number", "Document number", { placeholder: "e.g. HS-001" })}
          {field("owner", "Responsible person")}
          {field("effective_date", "Effective from", { type: "date" })}
          {field("last_reviewed", "Last reviewed", { type: "date" })}
          {field("review_date", "Next review date", { type: "date" })}
          <div>
            <Label className="text-xs text-slate-600">Review every</Label>
            <Select value={String(form.review_every_months || 12)} onValueChange={(v) => set("review_every_months", Number(v))}>
              <SelectTrigger data-testid="policy-field-review_every_months" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[3, 6, 12, 24, 36].map((m) => <SelectItem key={m} value={String(m)}>{m} months</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch data-testid="policy-field-is_active" checked={form.is_active !== false} onCheckedChange={(v) => set("is_active", v)} />
            <span className="text-sm text-slate-700">{form.is_active !== false ? "Active" : "Archived"}</span>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Summary / notes</Label>
          <Textarea data-testid="policy-field-description" value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1 rounded-sm border-slate-300" />
        </div>
        <p className="text-[11px] text-slate-500">&quot;Mark as reviewed today&quot; sets the next review date automatically using the review period.</p>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button data-testid="policy-save" className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={saving || !form.title?.trim()} onClick={save}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{policy?.id ? "Save changes" : `Add ${cfg.one}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
